import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { Calculator, TrendingDown, Loader2, Info } from "lucide-react";

/**
 * WastedCalculator — embeddable Wasted AI Visibility Calculator.
 *
 * Usage:
 *   <WastedCalculator defaultDomain="example.sk" compact={false} />
 *
 * `compact` shrinks the inputs into a single row and hides the breakdown
 * panel — used for embedding on the snapshot results page where we only
 * want the headline number to act as an upsell anchor.
 */
export default function WastedCalculator({
  defaultDomain = "",
  defaultVisibilityScore = null,
  compact = false,
  onCompute,
}) {
  const [defaults, setDefaults] = useState(null);
  const [domain, setDomain] = useState(defaultDomain);
  const [traffic, setTraffic] = useState(1000);
  const [cvr, setCvr] = useState(0.02);
  const [aov, setAov] = useState(40);
  const [score, setScore] = useState(defaultVisibilityScore ?? "");
  const [computing, setComputing] = useState(false);
  const [result, setResult] = useState(null);

  // Load defaults once.
  useEffect(() => {
    axios.get(`${API}/wasted-calculator/defaults`)
      .then((r) => {
        setDefaults(r.data);
        setTraffic(r.data.monthly_traffic);
        setCvr(r.data.conversion_rate);
        setAov(r.data.avg_order_value);
      })
      .catch(() => {/* defaults fail-safe */});
  }, []);

  const compute = useCallback(async () => {
    setComputing(true);
    try {
      const r = await axios.post(`${API}/wasted-calculator/compute`, {
        domain: domain || undefined,
        monthly_traffic: Number(traffic) || undefined,
        conversion_rate: Number(cvr) || undefined,
        avg_order_value: Number(aov) || undefined,
        ai_visibility_score: score === "" ? undefined : Number(score),
      });
      setResult(r.data);
      onCompute?.(r.data);
    } catch (e) {
      console.error("wasted compute failed", e);
    }
    setComputing(false);
  }, [domain, traffic, cvr, aov, score, onCompute]);

  // Auto-compute on mount once defaults arrived so the user immediately
  // sees a number (no empty state).
  useEffect(() => {
    if (defaults && !result) compute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults]);

  const fmt = (n) => {
    const v = Number(n) || 0;
    return v.toLocaleString("sk-SK", { maximumFractionDigits: 0 });
  };

  return (
    <section
      data-testid="wasted-calculator"
      className={`bg-white rounded-2xl border border-gray-200 ${compact ? "p-5" : "p-6 lg:p-8"}`}
    >
      <header className="mb-5">
        <h3 className={`font-bold text-gray-900 flex items-center gap-2 ${compact ? "text-lg" : "text-2xl"}`}>
          <Calculator className="w-6 h-6 text-red-600" />
          Kalkulačka stratených konverzií
        </h3>
        {!compact && (
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Koľko peňazí strácaš mesačne kvôli tomu, že ťa AI vyhľadávače nedostatočne citujú?
            Reálne čísla, podľa nezávislých 2026 dát.
          </p>
        )}
      </header>

      <div className={`grid gap-3 mb-5 ${compact ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"}`}>
        <NumberField
          label="Mesačná návštevnosť"
          value={traffic}
          onChange={setTraffic}
          suffix="/mes"
          testid="wasted-traffic"
        />
        <NumberField
          label="Konverzia (CVR)"
          value={cvr}
          onChange={setCvr}
          step={0.005}
          suffix=" %"
          format={(v) => (Number(v) * 100).toFixed(1)}
          parse={(v) => Number(v) / 100}
          testid="wasted-cvr"
        />
        <NumberField
          label="Priem. objednávka"
          value={aov}
          onChange={setAov}
          prefix="€"
          testid="wasted-aov"
        />
        <NumberField
          label="AI viditeľnosť (0–100)"
          value={score}
          onChange={setScore}
          placeholder="Nech doplníme zo skenu"
          testid="wasted-score"
        />
      </div>

      <button
        type="button"
        onClick={compute}
        disabled={computing}
        data-testid="wasted-compute-btn"
        className="w-full md:w-auto px-6 py-3 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
      >
        {computing ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Počítam stratu...
          </>
        ) : (
          <>
            <TrendingDown className="w-4 h-4" />
            Vypočítať stratu
          </>
        )}
      </button>

      {result && (
        <div data-testid="wasted-result" className="mt-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-100 rounded-xl p-5 text-center">
              <p className="text-xs font-bold text-red-700 uppercase tracking-widest mb-2">
                Mesačná strata
              </p>
              <p
                data-testid="wasted-monthly"
                className="text-5xl font-black text-red-600 tabular-nums"
              >
                €{fmt(result.monthly_lost_revenue)}
              </p>
              <p className="text-xs text-red-500 mt-2">
                ~{fmt(result.monthly_lost_orders)} stratených objednávok
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-xl p-5 text-center">
              <p className="text-xs font-bold text-gray-300 uppercase tracking-widest mb-2">
                Ročná strata
              </p>
              <p
                data-testid="wasted-annual"
                className="text-5xl font-black tabular-nums"
              >
                €{fmt(result.annual_lost_revenue)}
              </p>
              <p className="text-xs text-gray-300 mt-2">
                každý mesiac odkladu = ďalších €{fmt(result.monthly_lost_revenue)} preč
              </p>
            </div>
          </div>

          {!compact && (
            <details className="bg-gray-50 rounded-lg px-4 py-3 group">
              <summary className="cursor-pointer text-xs font-bold text-gray-600 uppercase tracking-wider flex items-center gap-2">
                <Info className="w-3.5 h-3.5" />
                Ako sa to počíta?
              </summary>
              <div className="mt-3 text-xs text-gray-600 space-y-1 font-mono">
                <div>monthly_traffic × ai_search_share × (1 − visibility/100) × cvr × aov</div>
                <div>{fmt(result.breakdown.monthly_traffic)} × {result.breakdown.ai_search_share} × {result.breakdown.ai_visibility_gap} × {result.breakdown.conversion_rate} × {result.breakdown.avg_order_value}</div>
                <div className="text-gray-500 font-sans pt-1">
                  Skóre zdroj: <strong>{result.breakdown.score_source}</strong> ·
                  AI search share 2026 default <strong>35 %</strong>.
                </div>
              </div>
            </details>
          )}

          {!compact && <EmailOptInForm amount={result.monthly_lost_revenue} />}
        </div>
      )}
    </section>
  );
}

/**
 * Post-result lead capture. Schedules a 24h follow-up email via the
 * `wasted_followup` drip (POST /api/wasted-calculator/notify), which
 * uses the same Resend pipeline as every other transactional email.
 *
 * Renders only after the calculator has a result so the offer is
 * anchored to a concrete pain number, not a hypothetical one.
 */
function EmailOptInForm({ amount }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // 'idle' | 'sending' | 'ok' | 'err'

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    setStatus("sending");
    try {
      const r = await axios.post(`${API}/wasted-calculator/notify`, {
        email,
        amount_eur: Math.round(amount || 0),
        lang: typeof document !== "undefined" && document.documentElement.lang === "sk" ? "sk" : "en",
      });
      setStatus(r.data?.ok ? "ok" : "err");
    } catch {
      setStatus("err");
    }
  };

  if (status === "ok") {
    return (
      <div
        data-testid="wasted-email-success"
        className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900"
      >
        ✅ Pošleme ti zajtra plán ako tieto straty zastaviť. Skontroluj si <strong>{email}</strong>.
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="wasted-email-form"
      className="rounded-xl border border-gray-200 bg-white px-5 py-4"
    >
      <p className="text-sm font-bold text-gray-900 mb-1">
        📧 Pošli mi 3-dňový akčný plán
      </p>
      <p className="text-xs text-gray-500 mb-3">
        Zajtra ti príde 1 email s konkrétnymi krokmi ako zastaviť stratu €{fmtSafe(amount)}/mes.
        Žiadny spam — kedykoľvek sa odhlásiš.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          required
          placeholder="tvoj@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="wasted-email-input"
          className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900"
        />
        <button
          type="submit"
          disabled={status === "sending"}
          data-testid="wasted-email-submit"
          className="px-4 py-2 text-sm font-bold text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
        >
          {status === "sending" ? "Posielam…" : "Poslať plán"}
        </button>
      </div>
      {status === "err" && (
        <p className="text-xs text-red-500 mt-2">Niečo sa pokazilo — skús to znova o chvíľu.</p>
      )}
    </form>
  );
}

function fmtSafe(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("sk-SK", { maximumFractionDigits: 0 });
}

function NumberField({
  label, value, onChange, prefix, suffix, step = 1, placeholder,
  format, parse, testid,
}) {
  const display = format ? format(value === "" ? "" : value) : value;
  return (
    <div className="min-w-0">
      <label className="block text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
        {label}
      </label>
      <div className="flex items-center bg-white border border-gray-200 rounded-lg focus-within:border-gray-900 min-w-0">
        {prefix && <span className="pl-3 text-sm text-gray-400 shrink-0">{prefix}</span>}
        <input
          type="text"
          inputMode="decimal"
          value={display}
          step={step}
          placeholder={placeholder}
          data-testid={testid}
          onChange={(e) => {
            const raw = e.target.value;
            const parsed = parse ? parse(raw) : raw;
            onChange(parsed === "" ? "" : parsed);
          }}
          className="flex-1 min-w-0 w-full px-2 py-2 text-sm font-mono bg-transparent focus:outline-none"
        />
        {suffix && <span className="pr-3 text-sm text-gray-400 shrink-0">{suffix}</span>}
      </div>
    </div>
  );
}
